import { useTags } from '@/hooks/use-tags';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Tag } from '@/types/tags';
import {
  ContentVisibility,
  type ContentVisibility as ContentVisibilityType,
  type VisibilityConfig,
  contentVisibilityLabels,
} from '@/types/content';

// SPEC-021: VisibilityPicker selects a visibility mode and — for tagged /
// assigned modes — gathers the audience config. Tag IDs come from the
// existing SPEC-017 tag service so a cohort tag doubles as a content
// audience selector.

interface VisibilityPickerProps {
  visibility: ContentVisibilityType;
  config: VisibilityConfig | null | undefined;
  onChange: (visibility: ContentVisibilityType, config: VisibilityConfig | null) => void;
}

const VISIBILITY_ORDER: ContentVisibilityType[] = [
  ContentVisibility.Public,
  ContentVisibility.TeacherStudents,
  ContentVisibility.Tagged,
  ContentVisibility.Assigned,
];

const VISIBILITY_DESCRIPTIONS: Record<ContentVisibilityType, string> = {
  public: 'Anyone on the platform can see this (subject to subscription tier).',
  teacher_students: 'Only students linked to your teacher account.',
  tagged: 'Students who carry one or more selected tags.',
  assigned: 'Specific students listed by user id.',
};

export function VisibilityPicker({ visibility, config, onChange }: VisibilityPickerProps) {
  const tagsQuery = useTags({ kind: 'label' });
  const labelTags = (tagsQuery.data ?? []) as Tag[];

  const selectVisibility = (next: ContentVisibilityType) => {
    if (next === ContentVisibility.Tagged) {
      onChange(next, { tag_ids: config?.tag_ids ?? [], match: config?.match ?? 'any' });
    } else if (next === ContentVisibility.Assigned) {
      onChange(next, { user_ids: config?.user_ids ?? [] });
    } else {
      onChange(next, null);
    }
  };

  const toggleTag = (tagId: string) => {
    const current = new Set(config?.tag_ids ?? []);
    if (current.has(tagId)) current.delete(tagId);
    else current.add(tagId);
    onChange(ContentVisibility.Tagged, {
      tag_ids: Array.from(current),
      match: config?.match ?? 'any',
    });
  };

  const setMatch = (match: 'any' | 'all') => {
    onChange(ContentVisibility.Tagged, { tag_ids: config?.tag_ids ?? [], match });
  };

  const setUserIDs = (raw: string) => {
    const ids = raw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    onChange(ContentVisibility.Assigned, { user_ids: ids });
  };

  return (
    <div className="space-y-3">
      <Label>Visibility</Label>
      <div className="grid gap-2 sm:grid-cols-2">
        {VISIBILITY_ORDER.map((mode) => {
          const selected = visibility === mode;
          return (
            <button
              type="button"
              key={mode}
              onClick={() => selectVisibility(mode)}
              className={cn(
                'rounded-md border p-3 text-left text-sm transition-colors',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-input hover:bg-accent',
              )}
            >
              <p className="font-medium">{contentVisibilityLabels[mode]}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {VISIBILITY_DESCRIPTIONS[mode]}
              </p>
            </button>
          );
        })}
      </div>

      {visibility === ContentVisibility.Tagged && (
        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium">Match:</span>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="tag-match"
                checked={(config?.match ?? 'any') === 'any'}
                onChange={() => setMatch('any')}
              />
              any tag
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="tag-match"
                checked={config?.match === 'all'}
                onChange={() => setMatch('all')}
              />
              all tags
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {tagsQuery.isLoading && <span className="text-xs text-muted-foreground">Loading tags…</span>}
            {!tagsQuery.isLoading && labelTags.length === 0 && (
              <span className="text-xs text-muted-foreground">
                No label tags available. Create one under CRM → Tags.
              </span>
            )}
            {labelTags.map((tag) => {
              const active = (config?.tag_ids ?? []).includes(tag.id);
              return (
                <button
                  type="button"
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-xs transition-colors',
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input hover:bg-accent',
                  )}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {visibility === ContentVisibility.Assigned && (
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="assigned-user-ids" className="text-xs">
            Student user IDs (comma or newline separated)
          </Label>
          <textarea
            id="assigned-user-ids"
            rows={3}
            defaultValue={(config?.user_ids ?? []).join('\n')}
            onBlur={(e) => setUserIDs(e.target.value)}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="uuid-1\nuuid-2"
          />
          <p className="text-xs text-muted-foreground">
            {(config?.user_ids ?? []).length} student(s) selected
          </p>
        </div>
      )}
    </div>
  );
}
